import { Injectable, computed, signal } from '@angular/core';

import { DeveloperRole, GlobalRole, MeResponse } from '@core/api/contracts';
import { CurrentUserProblemMessage } from './current-user-problem-detail.mapper';
import { CurrentUserBootstrapResult, CurrentUserState } from './current-user.models';

@Injectable({ providedIn: 'root' })
export class CurrentUserStore {
  private readonly state = signal<CurrentUserState>({
    sessionAccountKey: null,
    phase: 'idle',
    me: null,
    deniedReason: null,
  });
  private loadingRequest: Promise<CurrentUserBootstrapResult> | null = null;

  readonly sessionAccountKey = computed(() => this.state().sessionAccountKey);
  readonly phase = computed(() => this.state().phase);
  readonly me = computed(() => this.state().me);
  readonly deniedReason = computed(() => this.state().deniedReason);
  readonly isActive = computed(() => this.me()?.appState === 'ACTIVE');
  readonly requiresDeveloperOnboarding = computed(
    () => this.me()?.appState === 'ONBOARDING_REQUIRED' && this.me()?.requiredAction === 'CREATE_DEVELOPER'
  );
  readonly hasDeveloperContext = computed(() => {
    const me = this.me();
    return me?.appState === 'ACTIVE' && me.activeDeveloperId !== null;
  });

  snapshot(): CurrentUserState {
    return this.state();
  }

  resetForAccount(sessionAccountKey: string | null): void {
    this.loadingRequest = null;
    this.state.set({
      sessionAccountKey,
      phase: 'idle',
      me: null,
      deniedReason: null,
    });
  }

  clear(): void {
    this.resetForAccount(null);
  }

  beginLoading(sessionAccountKey: string, request: Promise<CurrentUserBootstrapResult>): void {
    this.loadingRequest = request;
    this.state.set({
      sessionAccountKey,
      phase: 'loading',
      me: null,
      deniedReason: null,
    });
  }

  getLoadingRequest(sessionAccountKey: string): Promise<CurrentUserBootstrapResult> | null {
    if (this.sessionAccountKey() !== sessionAccountKey || this.phase() !== 'loading') {
      return null;
    }

    return this.loadingRequest;
  }

  clearLoadingRequest(sessionAccountKey: string): void {
    if (this.sessionAccountKey() === sessionAccountKey) {
      this.loadingRequest = null;
    }
  }

  completeWithMe(sessionAccountKey: string, me: MeResponse): boolean {
    if (this.sessionAccountKey() !== sessionAccountKey) {
      return false;
    }

    this.loadingRequest = null;
    this.state.set({
      sessionAccountKey,
      phase: 'ready',
      me,
      deniedReason: null,
    });
    return true;
  }

  markUnauthenticated(sessionAccountKey: string | null): void {
    this.loadingRequest = null;
    this.state.set({
      sessionAccountKey,
      phase: 'unauthenticated',
      me: null,
      deniedReason: null,
    });
  }

  markAccessDenied(sessionAccountKey: string, deniedReason: CurrentUserProblemMessage): void {
    if (this.sessionAccountKey() !== sessionAccountKey) {
      return;
    }

    this.loadingRequest = null;
    this.state.set({
      sessionAccountKey,
      phase: 'access-denied',
      me: null,
      deniedReason,
    });
  }

  markError(sessionAccountKey: string): void {
    if (this.sessionAccountKey() !== sessionAccountKey) {
      return;
    }

    this.loadingRequest = null;
    this.state.update((state) => ({
      ...state,
      phase: 'error',
      me: null,
    }));
  }

  hasGlobalRole(role: GlobalRole): boolean {
    return this.me()?.globalRoles.includes(role) ?? false;
  }

  hasDeveloperRole(role: DeveloperRole): boolean {
    return this.me()?.developerRoles.includes(role) ?? false;
  }

  resolveDefaultRedirectUrl(): string {
    const me = this.me();

    if (this.phase() === 'access-denied') {
      return '/auth/access-denied';
    }
    if (!me) {
      return '/auth/login';
    }
    if (me.appState === 'ONBOARDING_REQUIRED' && me.requiredAction === 'CREATE_DEVELOPER') {
      return '/onboarding/developer';
    }
    if (me.appState === 'ACTIVE' && me.globalRoles.includes('ADMIN')) {
      return '/admin';
    }
    if (me.appState === 'ACTIVE' && me.activeDeveloperId !== null) {
      return '/home';
    }

    return '/auth/access-denied';
  }
}
